import {
  createEducationEndpointRequest,
  educationApiMethods,
} from './educationApiContract';

function unwrapEducationResponse(response) {
  // Unwrap HTTP transport envelope: { data: <body>, status: N }
  const body = (response && typeof response === 'object' && 'data' in response)
    ? response.data
    : response;

  // Unwrap backend success envelope: { success: true, data: <payload> }
  if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
    return body.data;
  }

  return body;
}

function resolveErrorPayload(error = {}) {
  return error.response?.data || error.data || error;
}

export class EducationApiClientError extends Error {
  constructor({
    cause,
    code = 'EDUCATION_API_ERROR',
    details = null,
    message = 'Education API request failed',
    methodName,
    request,
    status = null,
  } = {}) {
    super(message);
    this.name = 'EducationApiClientError';
    this.cause = cause;
    this.code = code;
    this.details = details;
    this.methodName = methodName;
    this.request = request;
    this.status = status;
  }
}

export function normalizeEducationApiError(error, { methodName, request } = {}) {
  const errorPayload = resolveErrorPayload(error);

  return new EducationApiClientError({
    cause: error,
    code: errorPayload.code || error.code || 'EDUCATION_API_ERROR',
    details: errorPayload.details || null,
    message: errorPayload.message || error.message || 'Education API request failed',
    methodName,
    request,
    status: error.response?.status || error.status || errorPayload.status || null,
  });
}

export function createEducationProductionApi({
  basePath = '/education',
  basePathByMethod = {},
  request,
} = {}) {
  if (typeof request !== 'function') {
    throw new Error('Education production api request is required');
  }

  const api = educationApiMethods.reduce((methods, methodName) => ({
    ...methods,
    [methodName]: async (...args) => {
      const endpointRequest = createEducationEndpointRequest(methodName, args, {
        basePath: basePathByMethod[methodName] || basePath,
      });

      try {
        return unwrapEducationResponse(await request(endpointRequest));
      } catch (error) {
        throw normalizeEducationApiError(error, {
          methodName,
          request: endpointRequest,
        });
      }
    },
  }), {});

  return {
    ...api,
    getQuizGames: (context = {}, payload = {}) => {
      const { includeAllStatuses, ...requestPayload } = payload;

      return api.getQuizGames(context, requestPayload);
    },
  };
}
